import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 400px;
  margin: 40px auto;
  padding: 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 24px;
  color: #2b3d5c;
  margin: 0 0 12px 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #2d5a27;
  }
`;

const Button = styled.button`
  padding: 12px;
  background: #2d5a27;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #1f3d1b;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  font-size: 14px;
  padding: 12px;
  border-radius: 6px;
  text-align: center;

  &.success {
    color: #2d5a27;
    background: #e8f5e9;
    border: 1px solid #c8e6c9;
  }

  &.error {
    color: #dc3545;
    background: #fff5f5;
    border: 1px solid #ffebee;
  }
`;

const BackLink = styled(Link)`
  display: block;
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-top: 20px;
  text-decoration: none;

  &:hover {
    color: #2d5a27;
    text-decoration: underline;
  }
`;

const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/too-many-requests':
      return 'Too many requests. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';
    default:
      return 'Unable to send reset email. Please try again.';
  }
};

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage({
        type: 'success',
        text: 'Password reset email sent. Please check your inbox (and spam folder).'
      });
      setEmail('');
    } catch (err) {
      console.error('Password reset error:', err.code, err.message);
      setMessage({
        type: 'error',
        text: getErrorMessage(err.code)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Title>Reset Password</Title>
      <Subtitle>
        Enter your email address and we'll send you a link to reset your password.
      </Subtitle>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Email Address</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />
        </FormGroup>

        <Button type="submit" disabled={loading}>
          {loading ? 'Sending...' : 'Send Reset Link'}
        </Button>

        {message.text && (
          <Message className={message.type}>
            {message.text}
          </Message>
        )}
      </Form>

      <BackLink to="/login">
        Back to Sign In
      </BackLink>
    </Container>
  );
};

export default ForgotPassword;
